import React, { useEffect, useRef } from "react";
import { Grid, TextField, Autocomplete } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/setup/redux/Store';
import { 
  fetchProvinces, 
  fetchWardsByProvince,
  selectProvinces,
  selectWardsByProvince,
  clearWards
} from '../redux/AddressRedux';
import { LabeledField } from '@/__core/components/LabeledField';
import { findHoChiMinhProvinceId } from '../utils/normalizeAddressFieldValue';

const resolveProvinceId = (province: unknown): string => {
  if (province == null) {
    return '';
  }
  if (typeof province === 'object' && 'id' in province) {
    return String((province as { id: string }).id ?? '');
  }
  return String(province);
};

const resolveWardId = (ward: unknown): string => {
  if (ward == null) {
    return '';
  }
  if (typeof ward === 'object' && 'id' in ward) {
    return String((ward as { id: string }).id ?? '');
  }
  return String(ward);
};

interface AddressFieldProps {
  value?: {
    address_line?: string;
    province?: string;
    ward?: string;
    free_address?: string;
  };
  onChange?: (value: { address_line?: string; province?: string; ward?: string; free_address?: string }) => void;
  onBlur?: () => void;
  label?: string;
  size?: "small" | "medium";
  isEditMode?: boolean;
  labelWidth?: number;
  styleDisplay?: "small" | "medium";
  disabled?: boolean;
  defaultProvinceOnEmpty?: boolean;
  sx?: any;
}

const AddressField: React.FC<AddressFieldProps> = ({
  value = {},
  onChange,
  onBlur,
  label,
  size = "small",
  isEditMode = true,
  labelWidth,
  styleDisplay,
  disabled,
  defaultProvinceOnEmpty = true,
  sx
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const provinces = useSelector(selectProvinces);
  const safeProvinces = Array.isArray(provinces) ? provinces : [];
  const selectedProvinceId = resolveProvinceId(value?.province);
  const selectedWardId = resolveWardId(value?.ward);
  const wards = useSelector((state: any) => selectWardsByProvince(state, selectedProvinceId));
  const safeWards = Array.isArray(wards) ? wards : [];

  // Simple refs to prevent infinite loops
  const hasLoadedProvinces = useRef(false);
  const hasSetDefault = useRef(false);
  const lastProvinceId = useRef<string>('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Load provinces only once
  useEffect(() => {
    if (!hasLoadedProvinces.current) {
      hasLoadedProvinces.current = true;
      dispatch(fetchProvinces());
    }
  }, []);

  // Auto-set default province to "Thành phố Hồ Chí Minh" on first load (create flow only).
  // In edit mode, injecting a default province mutates form state and falsely enables "Cập nhật".
  useEffect(() => {
    if (!defaultProvinceOnEmpty || !isEditMode || safeProvinces.length === 0 || hasSetDefault.current || selectedProvinceId) {
      return;
    }

    const hoChiMinhProvinceId = findHoChiMinhProvinceId(safeProvinces);
    if (!hoChiMinhProvinceId || !onChangeRef.current) {
      return;
    }

    hasSetDefault.current = true;
    onChangeRef.current({
      address_line: value?.address_line || '',
      province: hoChiMinhProvinceId,
      ward: '',
      free_address: value?.free_address || '',
    });
  }, [defaultProvinceOnEmpty, isEditMode, safeProvinces, selectedProvinceId, value?.address_line, value?.free_address]);

  // Fetch wards when province changes
  useEffect(() => {
    const currentProvinceId = selectedProvinceId;
    
    if (currentProvinceId && currentProvinceId !== lastProvinceId.current) {
      lastProvinceId.current = currentProvinceId;
      dispatch(fetchWardsByProvince(currentProvinceId));
    } else if (!currentProvinceId && lastProvinceId.current) {
      lastProvinceId.current = '';
      dispatch(clearWards());
    }
  }, [selectedProvinceId]); // Remove dispatch dependency

  // Simple event handlers - no memoization to avoid Unicode input issues
  const handleAddressLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      const newAddressLine = e.target.value;
      const selectedProvince = safeProvinces.find((province: any) => province.id === selectedProvinceId);
      const selectedWard = safeWards.find((ward: any) => ward.id === selectedWardId);
      
      // Auto-generate free_address when address_line changes
      let newFreeAddress = value?.free_address || '';
      if (newAddressLine && selectedProvince) {
        const parts = [newAddressLine];
        if (selectedWard) parts.push(selectedWard.name);
        parts.push(selectedProvince.name);
        newFreeAddress = parts.join(', ');
      }
      
      onChange({
        address_line: newAddressLine,
        province: selectedProvinceId,
        ward: selectedWardId,
        free_address: newFreeAddress,
      });
    }
  };

  const handleProvinceChange = (event: any, newValue: any) => {
    if (onChange) {
      const selectedProvinceId = newValue?.id || '';
      const selectedProvince = newValue;
      
      // Auto-generate free_address when province changes (without ward)
      let newFreeAddress = value?.free_address || '';
      if (selectedProvince && value?.address_line) {
        newFreeAddress = `${value.address_line}, ${selectedProvince.name}`;
      }
      
      onChange({
        address_line: value?.address_line || '',
        province: selectedProvinceId,
        ward: '', // Reset ward when province changes
        free_address: newFreeAddress,
      });
    }
  };

  const handleWardChange = (event: any, newValue: any) => {
    if (onChange) {
      const selectedWardId = newValue?.id || '';
      const selectedWard = newValue;
      const selectedProvince = safeProvinces.find((province: any) => province.id === selectedProvinceId);
      
      // Auto-generate free_address when ward changes
      let newFreeAddress = value?.free_address || '';
      if (selectedWard && selectedProvince) {
        const parts = [];
        if (value?.address_line) parts.push(value.address_line);
        parts.push(selectedWard.name);
        parts.push(selectedProvince.name);
        newFreeAddress = parts.join(', ');
      }
      
      onChange({
        address_line: value?.address_line || '',
        province: selectedProvinceId,
        ward: selectedWardId,
        free_address: newFreeAddress,
      });
    }
  };

  const handleFreeAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange({
        address_line: value?.address_line || '',
        province: selectedProvinceId,
        ward: selectedWardId,
        free_address: e.target.value,
      });
    }
  };

  return (
    <Grid container rowSpacing={0} columnSpacing={{ xs: 2, md: 4 }} sx={sx}>
      {/* Địa chỉ */}
      <Grid size={{ xs: 12, md: styleDisplay === 'small' ? 12 : 6 }}>
        <LabeledField
          label={label || 'Địa chỉ'}
          labelWidth={labelWidth}
        >
          <TextField
            fullWidth
            size={size}
            value={value?.address_line || ''}
            onChange={handleAddressLineChange}
            onBlur={onBlur}
            disabled={disabled || !isEditMode}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
        </LabeledField>
      </Grid>

      {/* Tỉnh/Thành phố */}
      <Grid size={{ xs: 12, md: styleDisplay === 'small' ? 6 : 3 }}>
        <LabeledField
          label="Tỉnh/Thành phố"
          labelWidth={labelWidth}
        >
          <Autocomplete
            fullWidth
            size={size}
            value={safeProvinces.find((province: any) => province.id === selectedProvinceId) || null}
            onChange={handleProvinceChange}
            onBlur={onBlur}
            options={safeProvinces}
            getOptionLabel={(option: any) => option.name || ''}
            isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
            disabled={disabled || !isEditMode}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder="Chọn tỉnh/thành phố"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                }}
              />
            )}
            noOptionsText="Không tìm thấy tỉnh/thành phố"
            clearOnEscape
            selectOnFocus
            handleHomeEndKeys
          />
        </LabeledField>
      </Grid>

      {/* Phường/Xã */}
      <Grid size={{ xs: 12, md: styleDisplay === 'small' ? 6 : 3 }}>
        <LabeledField
          
          label="Phường/Xã"
          labelWidth={labelWidth}
        >
          <Autocomplete
            className="Select__inline-icon"
            fullWidth
            size={size}
            value={safeWards.find((ward: any) => ward.id === selectedWardId) || null}
            onChange={handleWardChange}
            onBlur={onBlur}
            options={safeWards}
            getOptionLabel={(option: any) => option.name || ''}
            isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
            disabled={disabled || !isEditMode || !selectedProvinceId}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={!selectedProvinceId ? "Chọn tỉnh trước" : "Chọn phường/xã"}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                }}
              />
            )}
            noOptionsText="Không tìm thấy phường/xã"
            clearOnEscape
            selectOnFocus
            handleHomeEndKeys
          />
        </LabeledField>
      </Grid>

      {/* Địa chỉ tự do */}
      <Grid size={{ xs: 12 }}>
        <LabeledField
          label=" "
          labelWidth={labelWidth}
        >
          <TextField
            fullWidth
            size={size}
            value={value?.free_address || ''}
            onChange={handleFreeAddressChange}
            onBlur={onBlur}
            disabled={disabled || !isEditMode}
            placeholder="Địa chỉ tùy chọn"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
        </LabeledField>
      </Grid>
    </Grid>
  );
};

export default AddressField;
